import React from 'react';
import { createPortal } from 'react-dom';
import ProgramSlideCommunicationPanel from './ProgramSlideCommunicationPanel';
import {
  sendPresentationCommunicationMonitorCommand,
  type PresentationCommunicationMonitorSnapshot,
} from '../../utils/presentationCommunicationMonitorBridge';

type Props = {
  open: boolean;
  snapshot: PresentationCommunicationMonitorSnapshot | null;
  onClose: () => void;
};

/**
 * 원탭 전용: 프레젠테이션 의사소통 보드를 네트워크 없이 재표시.
 * 표시 토글(응답/이름/정답)은 패널 로컬 state라 프레젠테이션과 독립.
 */
const ProgramSlideCommunicationMonitorModal: React.FC<Props> = ({
  open,
  snapshot,
  onClose,
}) => {
  const [localGroupId, setLocalGroupId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open || !snapshot) return;
    setLocalGroupId(snapshot.selectedGroupId);
  }, [open, snapshot?.presentationRefId, snapshot?.selectedGroupId]);

  const effectiveGroupId =
    (localGroupId && snapshot?.groups.some((g) => g.id === localGroupId)
      ? localGroupId
      : snapshot?.selectedGroupId) ??
    snapshot?.groups[0]?.id ??
    null;

  const members = React.useMemo(() => {
    if (!snapshot || !effectiveGroupId) return [];
    return snapshot.membersByGroupId[effectiveGroupId] ?? [];
  }, [snapshot, effectiveGroupId]);

  if (!open || !snapshot) return null;

  const refId = snapshot.presentationRefId;
  const playState = snapshot.playState;
  const questionNavState = snapshot.questionNav;

  /** 여기서 누르면 원격(프레젠테이션 탭)에 명령만 보낸다 — 결과는 다음 스냅샷으로 반영됨 */
  const playControl = playState
    ? {
        isOpen: playState.isOpen,
        multiResponse: playState.multiResponse,
        canPlay: playState.canPlay,
        disabledReason: playState.disabledReason,
        onToggleMultiResponse: () =>
          sendPresentationCommunicationMonitorCommand(refId, 'toggleMultiResponse'),
        onPlay: () => sendPresentationCommunicationMonitorCommand(refId, 'play'),
        onStop: () => sendPresentationCommunicationMonitorCommand(refId, 'stop'),
      }
    : null;

  const questionNav = questionNavState
    ? {
        canPrev: questionNavState.canPrev,
        canNext: questionNavState.canNext,
        /** 모니터링에서는 삭제 불가(추가·편집만 가능) */
        canDelete: false,
        /** 클릭은 막되(canDelete: false) 흐림 없이 흰 원형 그대로 표시 */
        positionDisplayOnly: true,
        currentIndex: questionNavState.currentIndex,
        totalCount: questionNavState.totalCount,
        onPrev: () => sendPresentationCommunicationMonitorCommand(refId, 'prevQuestion'),
        onNext: () => sendPresentationCommunicationMonitorCommand(refId, 'nextQuestion'),
        onDeleteRequest: () => {},
        canAdd: Boolean(questionNavState.canAdd),
        onAdd: () => sendPresentationCommunicationMonitorCommand(refId, 'addQuestion'),
      }
    : null;

  return createPortal(
    <div
      className="cp-program-slide-comm-monitor-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="cp-program-slide-comm-monitor-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-program-slide-comm-monitor-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cp-program-slide-comm-monitor-header">
          <h2 id="cp-program-slide-comm-monitor-title" className="cp-program-slide-comm-monitor-title">
            질문 모니터링 및 리모트
          </h2>
          <button
            type="button"
            className="cp-program-slide-comm-monitor-close"
            aria-label="닫기"
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className="cp-program-slide-comm-monitor-body">
          <ProgramSlideCommunicationPanel
            mode="teacher"
            promptText={snapshot.promptText}
            answerText={snapshot.answerText}
            question={snapshot.question}
            members={members}
            responses={snapshot.responses}
            groups={snapshot.groups}
            selectedGroupId={effectiveGroupId}
            onSelectGroup={(groupId) => setLocalGroupId(groupId)}
            showDisabledMonitorButton
            onSaveQuestion={(question, promptText, answerText) =>
              sendPresentationCommunicationMonitorCommand(refId, 'saveQuestion', {
                question,
                promptText,
                answerText,
              })
            }
            playControl={playControl}
            questionNav={questionNav}
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProgramSlideCommunicationMonitorModal;
